import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, BadgeCheck, ShieldCheck, Camera, X } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";

export default function Profile() {
    const { user, updateUser } = useAuth();
    const fileRef = useRef(null);
    const [games, setGames] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/games").then(({ data }) => setGames(data.games));
        setForm({
            first_name: user.first_name || (user.name || "").split(" ")[0] || "",
            last_name: user.last_name || (user.name || "").split(" ").slice(1).join(" ") || "",
            bio: user.bio || "", region: user.region || "",
            main_game: user.main_game || "warzone", avatar_url: user.avatar_url || "",
        });
    }, [user]);

    if (!form) return <Loader label="Loading profile" />;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const initials = `${form.first_name[0] || ""}${form.last_name[0] || ""}`.toUpperCase() || "WW";

    const pickAvatar = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const dataUrl = await fileToResizedDataUrl(file, 256, 0.82);
            setForm((f) => ({ ...f, avatar_url: dataUrl }));
            toast.success("Photo ready — click Save Changes to apply.");
        } catch (err) { toast.error(err.message || "Could not load image."); }
        finally { e.target.value = ""; }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/auth/profile", form);
            updateUser(data);
            toast.success("Profile updated.");
        } catch { toast.error("Update failed."); }
        finally { setSaving(false); }
    };

    return (
        <div className="ww-fade-up max-w-3xl">
            <PageHeader title="Profile" subtitle="Manage your account and player identity." />

            <div className="bg-surface border border-border rounded-sm p-6 mb-6 flex items-center gap-5 ww-grid-bg">
                <div className="relative">
                    <Avatar className="w-20 h-20 rounded-sm border border-border">
                        <AvatarImage src={form.avatar_url} />
                        <AvatarFallback className="rounded-sm bg-volt/15 text-volt text-2xl font-bold">{initials}</AvatarFallback>
                    </Avatar>
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-btn"
                        className="absolute -bottom-1.5 -right-1.5 grid place-items-center w-7 h-7 rounded-sm bg-volt text-black hover:bg-[#33ceff]"><Camera className="w-3.5 h-3.5" /></button>
                    {form.avatar_url && (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, avatar_url: "" }))} data-testid="profile-avatar-clear"
                            className="absolute -top-1.5 -right-1.5 grid place-items-center w-5 h-5 rounded-full bg-alert text-white"><X className="w-3 h-3" /></button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickAvatar} data-testid="profile-avatar-input" />
                </div>
                <div className="min-w-0">
                    <div className="font-heading text-2xl font-black text-white truncate">{form.first_name} {form.last_name}</div>
                    <div className="text-sm text-muted-foreground font-mono truncate">{user.email}</div>
                    <div className="flex items-center gap-2 mt-2">
                        <StatusBadge status={user.is_owner ? "Owner" : (user.entitlements?.has_pro ? "Pro" : "Free")} />
                        {user.email_verified
                            ? <span className="inline-flex items-center gap-1 text-xs text-success font-mono"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-warn font-mono"><ShieldCheck className="w-3.5 h-3.5" /> Unverified</span>}
                    </div>
                </div>
            </div>

            <div className="bg-surface border border-border rounded-sm p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">First Name</Label>
                        <Input data-testid="profile-first-name" value={form.first_name} onChange={set("first_name")} className="bg-obsidian border-border rounded-sm h-10" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Last Name</Label>
                        <Input data-testid="profile-last-name" value={form.last_name} onChange={set("last_name")} className="bg-obsidian border-border rounded-sm h-10" />
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Region</Label>
                        <Input data-testid="profile-region" value={form.region} onChange={set("region")} placeholder="NA / EU / APAC" className="bg-obsidian border-border rounded-sm h-10" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Main Game</Label>
                        <Select value={form.main_game} onValueChange={(v) => setForm({ ...form, main_game: v })}>
                            <SelectTrigger className="bg-obsidian border-border rounded-sm h-10" data-testid="profile-game"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Avatar URL (optional)</Label>
                    <Input data-testid="profile-avatar-url" value={form.avatar_url?.startsWith("data:") ? "" : form.avatar_url}
                        onChange={set("avatar_url")} placeholder="https://...  (or use Upload Photo above)" className="bg-obsidian border-border rounded-sm h-10" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bio</Label>
                    <Textarea data-testid="profile-bio" value={form.bio} onChange={set("bio")} placeholder="Tell us about your playstyle..." className="bg-obsidian border-border rounded-sm min-h-[90px]" />
                </div>
                <button data-testid="save-profile" onClick={save} disabled={saving}
                    className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
                </button>
            </div>
        </div>
    );
}
